import { apiGet, operationsClient } from '@/lib/api';
import { estimateDocTemplateEndpoints } from '@/lib/api/endpoints';
import { estimateDocTemplateKeys } from '@/lib/queryKeys';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';

/**
 * Resolve the block layout for a design the operator has PICKED on a brand-new,
 * not-yet-saved estimate (the create flow), so the office preview reflects the
 * choice BEFORE the first save. Keyed on the design alone — there is no estimate
 * id yet — but it returns the same shape and runs through the same server
 * resolver as `GET /estimates/:id/document-layout`, so the pre-save preview
 * matches the post-save document.
 *
 * `previewDocTemplateId`: a template id, or `null` for the tenant default.
 * Returns `{ layout: null }` only when the tenant has no design at all (classic).
 */
export function useEstimateDesignPreviewLayout(
  previewDocTemplateId: string | null | undefined,
  enabled = true,
  opts?: { businessUnitId?: string | null; docType?: 'ESTIMATE' | 'INVOICE' },
) {
  const previewDesign =
    previewDocTemplateId === undefined ? undefined : (previewDocTemplateId ?? 'default');
  return useApiQuery<{ layout: EstimateDocLayout | null }>({
    queryKey: estimateDocTemplateKeys.designPreviewLayout(
      previewDesign ?? 'default',
      opts?.businessUnitId,
      opts?.docType,
    ),
    queryFn: () =>
      apiGet<{ layout: EstimateDocLayout | null }>(
        operationsClient,
        estimateDocTemplateEndpoints.previewLayout(previewDocTemplateId, {
          businessUnitId: opts?.businessUnitId,
          docType: opts?.docType,
        }),
      ),
    enabled: enabled && previewDesign !== undefined,
    staleTime: 60_000,
    retry: false,
    showErrorToast: false,
  });
}
